



























































import Vue from 'vue'
import CustomFieldInput from '@/components/CustomFieldInput.vue'
import extensionMixin from '../mixins/extension-options-mixin'
import { mapGetters } from 'vuex'
import { debounce } from 'debounce'
import {
    BasicQuery,
    BasicQueryType,
    BasicQueryWhere,
    GroupQuery
} from '@/types/Group.interface'
import ExtensionOption from '@/types/ExtensionOption.interface'
interface Operator {
    display: string
    statement: string
    operator: string
    slot: string
}
interface QueryProperty {
    display: string
    statement: string
    slot: string
    operators: Operator[]
    type: BasicQueryType
    has: string
    column?: string
    options?: string[]
    extension?: ExtensionOption
}
export default Vue.extend({
    mixins: [extensionMixin],
    components: { CustomFieldInput },
    props: {
        //  An existing query that will be loaded into the builder
        load: {
            type: Object,
            default: null
        },
        //  Disables chips
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            //  Holds each query 'chip' assembled by the user
            chips: [{ statement: 'SELECT Contacts' }],
            //  Indicates if the query is ready to be saved
            ready: false,
            //  The operators available to the query constructor
            operators: [
                {
                    display: 'Equals',
                    statement: 'EQUALS ',
                    operator: '=',
                    slot: 'operator'
                },
                {
                    display: 'Greater Than',
                    statement: 'IS GREATER THAN ',
                    operator: '>',
                    slot: 'operator'
                },
                {
                    display: 'Less Than',
                    statement: 'IS LESS THAN ',
                    operator: '<',
                    slot: 'operator'
                }
            ],
            //  Holds the query currently being assembled
            query: null,
            //  The currently selected person property
            selectedProperty: null,
            //  The currently selected property operator
            selectedOperator: null,
            //  The currently selected property value
            selectedValue: null,
            //  The total persons of the currently paginated query
            total: 0
        }
    },
    mounted() {
        //  If the 'load' query exists, load it
        if (this.load && this.load.queries.length > 0) return this.loadExistingQuery()

        //  Else, load a new empty query
        this.getTotal(this.newQuery())
    },
    computed: {
        ...mapGetters(['extensionOptions', 'hasInvoices', 'tenant']),
        //  Determines if the 'operator' select is visible
        visibleOperator(): boolean {
            //  If the selected property does NOT exist, we hide the operator select
            if (!this.selectedProperty) return false

            //  If the selected property does NOT have operators, we hide the operator
            if (!this.selectedProperty.operators) return false

            //  If the selected operator exists, we hide the select
            if (this.selectedOperator) return false

            //  Else, show the select
            return true
        },
        //  Determines if the query we're assembling is completed/valid
        isComplete(): boolean {
            //  If a query does NOT exist, return false
            if (!this.query) return false

            //  If a selected property does NOT exist, return false
            if (!this.selectedProperty) return false

            //  Create a list of 'complete' queries (ie query that require no WHERE clause)
            const completeQueries = ['has', 'user', 'not_user']

            //  Else, iterate all the queries in the query
            const sendableWheres = this.query.queries.filter((x: BasicQuery) => {
                //  If the query is a 'has' query, return true
                if (completeQueries.includes(x.type)) return true

                //  Else, iterate all the 'where' statements in the query
                return x.where.some((y: BasicQueryWhere) => {
                    //  Return complete 'where' statements
                    return y.column && y.operator && y.value
                })
            })

            //  Check if all 'where' queries are complete
            const isSendable = this.query.queries.length === sendableWheres.length

            //  Return the result
            return isSendable
        },
        //  Person properties available to the query builder
        personProps(): QueryProperty[] {
            //  Assemble defaults Person properties
            let defaults = [
                {
                    display: 'First Name',
                    statement: 'WHERE First Name',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'where',
                    column: 'name_given'
                },
                {
                    display: 'Last Name',
                    statement: 'WHERE Last Name',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'where',
                    column: 'name_family'
                },
                {
                    display: 'Middle Name',
                    statement: 'WHERE Middle Name',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'where',
                    column: 'name_middle'
                },
                {
                    display: 'Company',
                    statement: 'WHERE Company',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'where',
                    column: 'company'
                },
                {
                    display: 'Phone Number',
                    statement: 'WHERE Phone Number',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'whereHas',
                    has: 'phone_default',
                    column: 'msisdn'
                },
                {
                    display: 'Phone Area Code',
                    statement: 'WHERE Phone Area Code',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'whereHas',
                    has: 'phone_default',
                    column: 'area_code'
                },
                {
                    display: 'Address Line 1',
                    statement: 'WHERE Billing Address Line 1',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'whereHas',
                    has: 'addresses',
                    column: 'line1'
                },
                {
                    display: 'Address City',
                    statement: 'WHERE Billing Address City',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'whereHas',
                    has: 'addresses',
                    column: 'city'
                },
                {
                    display: 'Address State',
                    statement: 'WHERE Billing Address State',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'whereHas',
                    has: 'addresses',
                    column: 'country_subdivision'
                },
                {
                    display: 'Address Postalcode',
                    statement: 'WHERE Billing Address Postalcode',
                    slot: 'property',
                    operators: this.operatorsGet('text'),
                    type: 'whereHas',
                    has: 'addresses',
                    column: 'postalcode'
                },
                {
                    display: 'User',
                    statement: 'WHO are a User',
                    slot: 'property',
                    operators: null,
                    type: 'user'
                },
                {
                    display: 'Not User',
                    statement: 'WHO are NOT a User',
                    slot: 'property',
                    operators: null,
                    type: 'not_user'
                }
            ] as QueryProperty[]

            //  If the tenant has invoices, add invoice properties
            if (this.hasInvoices) {
                defaults = defaults.concat([
                    {
                        display: 'Invoice Balance',
                        statement: 'WHERE Invoice Balance',
                        slot: 'property',
                        operators: this.operatorsGet('number'),
                        type: BasicQueryType.whereHas,
                        has: 'invoices',
                        column: 'balance'
                    },
                    {
                        display: 'Invoice Paid At',
                        statement: 'WHERE Invoice Paid At',
                        slot: 'property',
                        operators: this.operatorsGet('date'),
                        type: BasicQueryType.whereHas,
                        has: 'invoices',
                        column: 'paid_at'
                    },
                    {
                        display: 'Invoice Total',
                        statement: 'WHERE Invoice Total',
                        slot: 'property',
                        operators: this.operatorsGet('number'),
                        type: BasicQueryType.whereHas,
                        has: 'invoices',
                        column: 'total'
                    }
                ])
            }

            //  Assemble an array of custom ExtensionOption properties
            const extensions = this.extensionOptions.map((x: ExtensionOption) => ({
                display: x.name,
                statement: `WHERE ${x.name}`,
                slot: 'property',
                operators: this.operatorsGet(x.type),
                type: 'where',
                column: `extensions->${x.name}`,
                options: x.data,
                extension: x
            }))

            //  Merge extension option properties with defaults
            const props = defaults.concat(extensions)

            //  Return the available person properties
            return props
        }
    },
    methods: {
        //  Assemble the query based on selected properties
        assembleQuery(): void {
            //  Get the selected property
            const prop = this.selectedProperty

            //  If a selected property does NOT exist, reset 'ready' state, load default empty query
            if (!prop) {
                this.ready = false
                return this.getTotal(this.newQuery())
            }

            //  Else, define the default state of our WHERE clause
            const where = []

            //  If the selected property requires a WHERE clause, add it
            if (prop.column) {
                where.push({
                    column: prop.column || null,
                    operator: this.selectedOperator?.operator || null,
                    value: this.selectedValue || null
                })
            }

            //  Update the query we're assembling
            this.query = {
                exceptions: [],
                queries: [
                    {
                        type: prop.type,
                        has: prop.has || null,
                        where: where
                    }
                ]
            }

            //  If the query is complete/valid...
            if (this.isComplete) {
                //  Send the complete query to the parent
                this.$emit('querySelected', this.query)

                //  Fetch the person count for the valid query
                return this.getTotal(this.query)
            }

            //  Else reset 'total' and 'ready' state
            this.total = 0
            this.ready = false
        },
        //  Reset the query being assembled
        clearQuery(): void {
            //  Reset the query
            this.query = this.newQuery()

            //  Reset chips
            this.chips = [{ statement: 'SELECT Contacts' }]

            //  Reset all selected values
            this.selectedProperty = null
            this.selectedOperator = null
            this.selectedValue = null

            //  Get the new (ie default) total
            this.getTotal(this.query)
        },
        //  Get the total person count for a valid query
        getTotal: debounce(async function (query: GroupQuery): Promise<void> {
            //  Assemble request params
            const params = { query: query }

            //  Get the total from the API
            const total = await this.$store.dispatch('personsGetQueryCount', params)

            //  Set the member total
            this.total = total

            //  Evaluate the 'ready' state
            this.ready = this.isComplete
        }, 600),
        //  Load an existing query into the builder
        loadExistingQuery(): void {
            //  Get the existing query (in v1.4.3 we only handle a single query)
            const q = this.load.queries[0]

            if (q.type === 'has') {
                //  Find the selected property
                const p = this.personProps.find((x: QueryProperty) => x.has === q.has)

                //  Set the selected property
                this.onPropertySelected(p)
            }

            if (q.type === 'user') {
                //  Find the selected property
                const p = this.personProps.find((x: QueryProperty) => x.type === 'user')

                //  Set the selected property
                this.onPropertySelected(p)
            }

            if (q.type === 'not_user') {
                //  Find the selected property
                const p = this.personProps.find(
                    (x: QueryProperty) => x.type === 'not_user'
                )

                //  Set the selected property
                this.onPropertySelected(p)
            }

            if (q.type === 'where') {
                //  Get the where clause (in v1.4.3 we only handle a single where clause)
                const w = q.where[0]

                //  Find the person property that matches the column
                const p = this.personProps.find(
                    (x: QueryProperty) => x.column === w.column
                )

                //  Find the operator that matches the WHERE clause
                const operator = this.operators.find(
                    (x: Operator) => x.operator === w.operator
                )

                this.onPropertySelected(p)
                this.onOperatorSelected(operator)
                this.onValueSelected(w.value)
            }

            if (q.type === 'whereHas') {
                //  Get the where clause (in v1.4.3 we only handle a single where clause)
                const w = q.where[0]

                //  Find the person property that matches the column
                const p = this.personProps.find(
                    (x: QueryProperty) => x.has === q.has && x.column === w.column
                )

                //  Find the operator that matches the WHERE clause
                const operator = this.operators.find((x) => x.operator === w.operator)

                this.onPropertySelected(p)
                this.onOperatorSelected(operator)
                this.onValueSelected(w.value)
            }
        },
        //  Create an empty query object
        newQuery(): GroupQuery {
            return {
                exceptions: [],
                queries: []
            }
        },
        //  Handler for when a user selects a person property
        onPropertySelected(property: QueryProperty): void {
            //  Set the selected property
            this.selectedProperty = property

            //  Add the property to the statement chips
            this.chips.push(property)

            //  Update the query we're assembling
            this.assembleQuery()
        },
        //  Handler for when a user selects an operator
        onOperatorSelected(operator: Operator): void {
            //  Set the selected operator
            this.selectedOperator = operator

            //  Add the operator to the statement chips
            this.chips.push(operator)
        },
        //  Handler for when a user selects/inputs a value
        onValueSelected(value: string): void {
            //  Reset the ready state (since the query has changed)
            this.ready = false

            // Set the currently selected value
            this.selectedValue = value

            //  Assemble the 'value' statement chip
            const chip = { statement: value, slot: 'value' }

            //  Check if the 'value' chip already exists
            const i = this.chips.findIndex((x) => x.slot === 'value')

            //  If the value is NULL or Empty, remove the existing value chip
            if (!value && i > -1) this.chips.splice(i, 1)

            //  If the value AND the chip exists, update it
            if (value && i > -1) this.chips[i] = chip

            //  If the value exists AND the chip does NOT exist, add the chip
            if (value && i === -1) this.chips.push(chip)

            //  Update the query we're assembling
            this.assembleQuery()
        },
        //  Handler for when a user removes a chip from the chips
        onItemRemoved(item: QueryProperty): void {
            //  Remove the item and all items after it
            const i = this.chips.indexOf(item)
            this.chips.splice(i, this.chips.length - i)

            //  If the item removed is a 'property' reset all selected
            if (item.slot === 'property') {
                this.selectedProperty = null
                this.selectedOperator = null
                this.selectedValue = null
            }

            //  If the item removed is a 'operator' reset operator and value
            if (item.slot === 'operator') {
                this.selectedOperator = null
                this.selectedValue = null

                //  If a custom input is being used, reset it
                if (this.$refs.customInput) this.$refs.customInput.reset()
            }

            //  If the item removed is a 'value' reset value
            if (item.slot === 'value') {
                this.selectedValue = null

                //  If a custom input is being used, reset it
                if (this.$refs.customInput) this.$refs.customInput.reset()
            }

            this.assembleQuery()
        },
        //  Get the appropriate operators based on property type
        operatorsGet(type: string): Operator[] {
            //  Craete a object switch to select operators by type
            const operators = {
                dropdown: this.operators.filter((x) => x.operator === '='),
                text: this.operators.filter((x) => x.operator === '='),
                number: this.operators,
                phone: this.operators.filter((x) => x.operator === '='),
                date: [
                    {
                        display: 'Is After',
                        statement: 'IS AFTER',
                        operator: '>',
                        slot: 'operator'
                    },
                    {
                        display: 'Is Before',
                        statement: 'IS BEFORE',
                        operator: '<',
                        slot: 'operator'
                    }
                ]
            }[type]

            return operators
        },
        //  Pop the members help message
        popMembersHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Filtered Contacts Total',
                text: `This is the number of contacts that match your filters. This number will update as you add/remove filters.`,
                icon: 'bi-filter-circle-fill',
                color: 'primary'
            })
        }
    },
    watch: {
        ready(val: boolean): void {
            //  Send the current validation to the parent (used to disable the 'save' button)
            this.$emit('validated', val)
        }
    }
})
